import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, authedFetch } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  CheckCircle2,
  XCircle,
  Clock,
  FolderOpen,
  Building2,
} from "lucide-react";

interface InviteDetails {
  email: string;
  role: string;
  project: { id: string; name: string; description: string | null } | null;
  tenant: { id: string; name: string } | null;
  expiresAt: string | null;
}

export function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!token) return;
    authedFetch(`/api/invites/${token}`)
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) {
          setError(data.error ?? "Failed to load invite");
          setErrorStatus(data.status ?? null);
        } else {
          setInvite(data as InviteDetails);
        }
      })
      .catch(() => setError("Failed to load invite"))
      .finally(() => setIsLoading(false));
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    setIsAccepting(true);
    try {
      const res = await authedFetch(`/api/invites/${token}/accept`, {
        method: "POST",
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error ?? "Failed to accept invite");
        setIsAccepting(false);
        return;
      }
      setAccepted(true);
      toast.success("Invite accepted!");
      // Navigate to project if project-scoped, or to projects for workspace
      if (data.project?.id) {
        setTimeout(() => {
          window.location.href = `/projects/${data.project.id}`;
        }, 1500);
      } else {
        setTimeout(() => {
          window.location.href = "/projects";
        }, 1500);
      }
    } catch {
      toast.error("Failed to accept invite");
      setIsAccepting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4">
          <Skeleton className="h-8 w-48 mx-auto" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-md text-center space-y-4">
          {errorStatus === "accepted" ? (
            <CheckCircle2 className="mx-auto h-12 w-12 text-muted-foreground" />
          ) : errorStatus === "expired" ? (
            <Clock className="mx-auto h-12 w-12 text-muted-foreground" />
          ) : (
            <XCircle className="mx-auto h-12 w-12 text-destructive" />
          )}
          <h1 className="text-xl font-semibold">{error}</h1>
          {errorStatus === "accepted" && (
            <p className="text-sm text-muted-foreground">
              This invite has already been used.
            </p>
          )}
          {errorStatus === "expired" && (
            <p className="text-sm text-muted-foreground">
              Ask the workspace owner to send a new invite.
            </p>
          )}
          <Button variant="outline" onClick={() => navigate("/projects")}>
            Go to Projects
          </Button>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-md text-center space-y-4">
          <CheckCircle2 className="mx-auto h-12 w-12 text-finding-helps" />
          <h1 className="text-xl font-semibold">You&apos;re in!</h1>
          <p className="text-sm text-muted-foreground">
            Redirecting to{" "}
            {invite?.project?.name ?? invite?.tenant?.name ?? "the workspace"}
            ...
          </p>
        </div>
      </div>
    );
  }

  const emailMismatch =
    auth?.email &&
    invite?.email &&
    auth.email.toLowerCase() !== invite.email.toLowerCase();

  const isWorkspaceInvite = !invite?.project && !!invite?.tenant;

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-xl font-semibold tracking-tight">
            {isWorkspaceInvite ? "Workspace invite" : "Project invite"}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isWorkspaceInvite
              ? `You've been invited to join the ${invite.tenant?.name ?? "workspace"}`
              : "You've been invited to join a project"}
          </p>
        </div>

        <div className="rounded-lg border bg-card p-6 space-y-4">
          {invite?.project ? (
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <FolderOpen className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="font-semibold">{invite.project.name}</h2>
                {invite.project.description && (
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    {invite.project.description}
                  </p>
                )}
              </div>
            </div>
          ) : invite?.tenant ? (
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="font-semibold">{invite.tenant.name}</h2>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Workspace Team Member
                </p>
              </div>
            </div>
          ) : null}

          <div className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2">
            <span className="text-sm text-muted-foreground">Role</span>
            <Badge variant="secondary" className="capitalize">
              {invite?.role}
            </Badge>
          </div>

          <div className="flex items-center justify-between gap-2 rounded-md bg-muted/50 px-3 py-2">
            <span className="text-sm text-muted-foreground shrink-0">
              Invited as
            </span>
            <span className="text-sm truncate max-w-[200px] sm:max-w-none">
              {invite?.email}
            </span>
          </div>

          {emailMismatch ? (
            <div className="rounded-md border border-amber-500/20 bg-amber-500/10 p-3 space-y-1.5 text-xs">
              <p className="text-muted-foreground">
                This invitation was sent to{" "}
                <strong className="text-foreground">{invite?.email}</strong>.
              </p>
              <p className="text-muted-foreground">
                You are signed in as{" "}
                <strong className="text-foreground">{auth?.email}</strong>. You
                will join this workspace with your current account.
              </p>
            </div>
          ) : null}

          <Button
            className="w-full"
            onClick={handleAccept}
            disabled={isAccepting}
          >
            {isAccepting
              ? "Accepting..."
              : emailMismatch
                ? `Join as ${auth?.displayName || auth?.email}`
                : "Accept Invite"}
          </Button>

          {emailMismatch && (
            <div className="text-center pt-1">
              <a
                href={`/api/auth/logout?redirect=${encodeURIComponent(
                  window.location.pathname + window.location.search,
                )}`}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-4"
              >
                Sign in with a different account instead
              </a>
            </div>
          )}
        </div>

        <div className="text-center">
          <Button
            variant="link"
            className="text-sm text-muted-foreground"
            onClick={() => navigate("/projects")}
          >
            Decline and go back
          </Button>
        </div>
      </div>
    </div>
  );
}
